// Customer Intelligence (Intelligence Suite) permission catalog — client mirror of the
// backend Role::CUSTOMER_INTELLIGENCE_* constants (perx-api). Keys are frozen on the wire
// (v4_roles.permissions jsonb); renaming any key here without a BE migration breaks saved
// roles. Labels are FR v30 §4.5–4.8. PLA-8807.

import _startCase from 'lodash/startCase';

export type IndustrySlug = 'banking_bfsi' | 'telco' | 'insurance' | 'fintech_wallet_payment';

export type CIActionVerb = 'view' | 'send_to_growth_planner' | 'use';

export type CIScreen = 'customer_intelligence.overview' | 'customer_intelligence.customer360';

export interface ICustomerIntelligencePermission {
  resource_name: string;
  actions: string[];
  restricted_actions: string[];
  restricted_fields: string[];
}

export const CI_ROOT = 'customer_intelligence';

// Side-menu module label (FR v30 §2/§4.3). Distinct from CI_LABELS[CI_ROOT] ('View
// Intelligence Suite'), which is the root view-action checkbox label, not the menu entry.
export const CI_MENU_LABEL = 'Intelligence Suite';

export const CI_AI_ASSISTANT = `${CI_ROOT}.ai_assistant`;

// Side-menu label for the Orbit grant when it renders on its own, outside the Intelligence Suite
// tree (Intelligence Suite flag off, Perx Orbit flag on). PLA-9754.
export const CI_ORBIT_MENU_LABEL = 'Perx Orbit';

// resource_name → FR v30 display label. Keyed by the FULL dotted key because the same
// leaf segment (e.g. behavioural_intelligence) carries a different label under overview
// vs customer360. Exhaustive over the BE catalog; labelOf() falls back to startCase for
// any key the BE adds ahead of this map.
export const CI_LABELS: Record<string, string> = {
  customer_intelligence: 'View Intelligence Suite',
  'customer_intelligence.overview': 'View Customer Insights',
  'customer_intelligence.customer360': 'View Customer 360',
  'customer_intelligence.ai_assistant': 'Use Perx Orbit for Intelligence Suite',

  'customer_intelligence.overview.data_trust_integration_health':
    'View Data Trust and Integration Health',
  'customer_intelligence.overview.executive_kpis': 'View Executive KPIs',
  'customer_intelligence.overview.customer_lifecycle_holdings':
    'View Customer Lifecycle and Holdings',
  'customer_intelligence.overview.behavioural_intelligence': 'View Behavioural Intelligence',
  'customer_intelligence.overview.engagement_intelligence': 'View Engagement Intelligence',
  'customer_intelligence.overview.campaign_journey_intelligence':
    'View Campaign and Journey Intelligence',
  'customer_intelligence.overview.reward_loyalty_intelligence':
    'View Reward and Loyalty Intelligence',
  'customer_intelligence.overview.risk_fraud_anomaly_intelligence':
    'View Risk, Fraud and Anomaly Intelligence',
  'customer_intelligence.overview.sentiment_expectation_intelligence':
    'View Sentiment and Expectation Intelligence',
  'customer_intelligence.overview.customer_value_clv_intelligence':
    'View Customer Value and CLV Intelligence',
  'customer_intelligence.overview.opportunity_propensity_intelligence':
    'View Opportunity and Propensity Intelligence',
  'customer_intelligence.overview.nudge_readiness': 'View Nudge Readiness',
  'customer_intelligence.overview.do_not_nudge_guardrails': 'View Do-Not-Nudge Guardrails',
  'customer_intelligence.overview.signal_handoff_readiness': 'View Signal and Handoff Readiness',
  'customer_intelligence.overview.product_level_readiness': 'View Product-Level Readiness',

  'customer_intelligence.customer360.profile_summary': 'View Customer Profile Summary',
  'customer_intelligence.customer360.customer_details': 'View Customer Details',
  'customer_intelligence.customer360.holdings_value': 'View Customer Holdings and Value',
  'customer_intelligence.customer360.behavioural_intelligence':
    'View Customer Behavioural Intelligence',
  'customer_intelligence.customer360.engagement_timeline': 'View Customer Engagement Timeline',
  'customer_intelligence.customer360.reward_loyalty_intelligence':
    'View Customer Reward and Loyalty Intelligence',
  'customer_intelligence.customer360.risk_fraud_anomaly_intelligence':
    'View Customer Risk, Fraud and Anomaly Intelligence',
  'customer_intelligence.customer360.model_confidence_evidence':
    'View Customer Model Confidence and Evidence',
  'customer_intelligence.customer360.sentiment_expectation_intelligence':
    'View Customer Sentiment and Expectation Intelligence',
  'customer_intelligence.customer360.opportunity_propensity_intelligence':
    'View Customer Opportunity and Propensity Intelligence',
  'customer_intelligence.customer360.nudge_readiness': 'View Customer Nudge Readiness',
  'customer_intelligence.customer360.do_not_nudge_guardrails':
    'View Customer Do-Not-Nudge Guardrails',
  'customer_intelligence.customer360.financial_intelligence':
    'View Customer Financial Intelligence',
  'customer_intelligence.customer360.product_level_readiness':
    'View Customer Product-Level Readiness',

  'customer_intelligence.overview.industry': 'View Industry-Specific Intelligence',
  'customer_intelligence.customer360.industry': 'View Customer Industry-Specific Intelligence',

  'customer_intelligence.overview.industry.payment_behaviour_cash_usage':
    'View Payment Behaviour & Cash Usage',
  'customer_intelligence.overview.industry.merchant_category_data_quality':
    'View Merchant & Category Data Quality',
  'customer_intelligence.overview.industry.merchant_category_preferences':
    'View Merchant & Category Preferences',
  'customer_intelligence.overview.industry.income_cashflow_health': 'View Income & Cashflow Health',
  'customer_intelligence.overview.industry.debt_spending_health': 'View Debt & Spending Health',
  'customer_intelligence.overview.industry.churn_service_experience':
    'View Churn & Service Experience',
  'customer_intelligence.overview.industry.plan_usage_revenue': 'View Plan Usage & Revenue',
  'customer_intelligence.overview.industry.recharge_upgrade_readiness':
    'View Recharge & Upgrade Readiness',
  'customer_intelligence.overview.industry.policy_health_claims': 'View Policy Health & Claims',
  'customer_intelligence.overview.industry.renewal_growth_opportunities':
    'View Renewal & Growth Opportunities',
  'customer_intelligence.overview.industry.wallet_activity_funding':
    'View Wallet Activity & Funding',
  'customer_intelligence.overview.industry.payment_usage_risk': 'View Payment Usage & Risk',

  'customer_intelligence.customer360.industry.cash_usage_dependency':
    'View Cash Usage & Dependency',
  'customer_intelligence.customer360.industry.transaction_data_quality':
    'View Transaction Data Quality',
  'customer_intelligence.customer360.industry.payment_merchant_behaviour':
    'View Payment & Merchant Behaviour',
  'customer_intelligence.customer360.industry.churn_service_experience':
    'View Churn & Service Experience',
  'customer_intelligence.customer360.industry.plan_usage_upgrade_readiness':
    'View Plan Usage & Upgrade Readiness',
  'customer_intelligence.customer360.industry.policy_health_claims': 'View Policy Health & Claims',
  'customer_intelligence.customer360.industry.renewal_coverage_opportunities':
    'View Renewal & Coverage Opportunities',
  'customer_intelligence.customer360.industry.wallet_activity_adoption':
    'View Wallet Activity & Adoption',
  'customer_intelligence.customer360.industry.payment_usage_risk': 'View Payment Usage & Risk'
};

export const CI_ACTION_LABELS: Record<CIActionVerb, string> = {
  view: 'View',
  send_to_growth_planner: 'Send to Growth Planner',
  use: 'Use Perx Orbit'
};

export const INDUSTRY_DISPLAY: Record<IndustrySlug, string> = {
  banking_bfsi: 'Banking/BFSI',
  telco: 'Telecom',
  insurance: 'Insurance',
  fintech_wallet_payment: 'Fintech/Wallet'
};

export const CI_COPY = {
  bannerAiLayer:
    "AI Assistant is managed by layer. Intelligence Suite chatbot access follows the user's permissions.",
  bannerMissingCard:
    'Missing card permission hides the complete card and omits its data from APIs.',
  bannerRemoveParent: 'Removing a parent clears its child selections after confirmation.',
  overallAccessPill:
    'Disabling "View Intelligence Suite" clears all child selections after confirmation.',
  sgpHelper: 'No separate permissions for filters or drill-down.',
  aiChatbotHelper:
    "Applies only to Intelligence Suite and inherits the user's screen, metric, sensitive-data and action permissions.",
  industryNotConfiguredTitle: 'Industry not configured',
  industryNotConfiguredBody:
    'Set an industry in Settings → Localization to enable industry-specific permissions.'
};

// Mirrors Role::CUSTOMER_INTELLIGENCE_INDUSTRY_PERMISSIONS but excludes `*.product_level_readiness`
// (shared screen-level cards that always render; industry filter must not gate them — PLA-8807).
export const INDUSTRY_KEYS: Record<IndustrySlug, string[]> = {
  banking_bfsi: [
    'customer_intelligence.overview.industry.payment_behaviour_cash_usage',
    'customer_intelligence.overview.industry.merchant_category_data_quality',
    'customer_intelligence.overview.industry.merchant_category_preferences',
    'customer_intelligence.overview.industry.income_cashflow_health',
    'customer_intelligence.overview.industry.debt_spending_health',
    'customer_intelligence.customer360.industry.cash_usage_dependency',
    'customer_intelligence.customer360.industry.transaction_data_quality',
    'customer_intelligence.customer360.industry.payment_merchant_behaviour'
  ],
  telco: [
    'customer_intelligence.overview.industry.churn_service_experience',
    'customer_intelligence.overview.industry.plan_usage_revenue',
    'customer_intelligence.overview.industry.recharge_upgrade_readiness',
    'customer_intelligence.customer360.industry.churn_service_experience',
    'customer_intelligence.customer360.industry.plan_usage_upgrade_readiness'
  ],
  insurance: [
    'customer_intelligence.overview.industry.policy_health_claims',
    'customer_intelligence.overview.industry.renewal_growth_opportunities',
    'customer_intelligence.customer360.industry.policy_health_claims',
    'customer_intelligence.customer360.industry.renewal_coverage_opportunities'
  ],
  fintech_wallet_payment: [
    'customer_intelligence.overview.industry.payment_behaviour_cash_usage',
    'customer_intelligence.overview.industry.merchant_category_data_quality',
    'customer_intelligence.overview.industry.merchant_category_preferences',
    'customer_intelligence.overview.industry.wallet_activity_funding',
    'customer_intelligence.overview.industry.payment_usage_risk',
    'customer_intelligence.customer360.industry.cash_usage_dependency',
    'customer_intelligence.customer360.industry.transaction_data_quality',
    'customer_intelligence.customer360.industry.payment_merchant_behaviour',
    'customer_intelligence.customer360.industry.wallet_activity_adoption',
    'customer_intelligence.customer360.industry.payment_usage_risk'
  ]
};

export const isCIKey = (resourceName: string): boolean =>
  resourceName === CI_ROOT || resourceName.startsWith(`${CI_ROOT}.`);

// CI descendant keys actually rendered for `industry`: catalog CI descendants minus the
// other-industry `*.industry.*` children Unit 3 keeps as hidden preserved grants. This drives
// what the tree renders and gates; it is deliberately NOT the cascade-clear set, which works on
// the stored keys so a confirmed "clear all beneath this" leaves nothing behind (PLA-9156).
export const renderedCiDescendants = (
  resourceNames: readonly string[],
  industry: string | null | undefined
): string[] =>
  resourceNames.filter(
    name =>
      name.startsWith(`${CI_ROOT}.`) &&
      (!name.includes('.industry.') ||
        (!!industry &&
          industry in INDUSTRY_KEYS &&
          INDUSTRY_KEYS[industry as IndustrySlug].includes(name)))
  );

export const childrenOf = (resourceName: string, catalog: readonly string[]): string[] => {
  const prefix = `${resourceName}.`;
  return catalog.filter(key => key.startsWith(prefix) && !key.slice(prefix.length).includes('.'));
};

export const screenOf = (resourceName: string): CIScreen | null => {
  if (!isCIKey(resourceName)) return null;
  const screen = resourceName.split('.')[1];
  return screen === 'overview' || screen === 'customer360' ? `${CI_ROOT}.${screen}` : null;
};

export const labelOf = (resourceName: string): string =>
  CI_LABELS[resourceName] ?? _startCase(resourceName.split('.').pop() ?? resourceName);

// --- Customer 360 prerequisites -------------------------------------------------------------
// Client mirror of Role#customer360_prerequisites (perx-api origin/uat app/models/role.rb:273).
// The backend counts a resource as granted only when its actions include `view`, so every
// resource these helpers return must be granted `view` specifically, not merely be present.

export const CUSTOMER_MODULE_RESOURCE = 'customer_management';

export const CUSTOMER360_RESOURCE = `${CI_ROOT}.customer360`;

export const CUSTOMER360_INDUSTRY_RESOURCE = `${CUSTOMER360_RESOURCE}.industry`;

export const CUSTOMER360_CONTAINER_RESOURCES = [CI_ROOT, CUSTOMER360_RESOURCE];

// Customer 360 cards that render as plain base cards but whose backend permission_chain still
// appends the `.industry` container, because Catalog#permission_chain adds it for every
// industry-scoped metric (perx-api app/lib/customer_intelligence/catalog.rb:136). Ticking one of
// these without the Industry row is the one combination the tree allows but the BE rejects.
export const CUSTOMER360_INDUSTRY_SCOPED_LEAVES = [
  `${CUSTOMER360_RESOURCE}.financial_intelligence`,
  `${CUSTOMER360_RESOURCE}.product_level_readiness`
];

// Every dotted prefix of the leaf, plus the industry container when the leaf is industry-scoped.
// The leaf itself is dropped: the backend keeps it in the chain but it is granted by definition.
const containersForCustomer360Leaf = (leaf: string): string[] => {
  const segments = leaf.split('.');
  const chain = segments.map((_, depth) => segments.slice(0, depth + 1).join('.'));
  if (CUSTOMER360_INDUSTRY_SCOPED_LEAVES.includes(leaf)) {
    chain.push(CUSTOMER360_INDUSTRY_RESOURCE);
  }
  return chain.filter(name => name !== leaf);
};

// Given the resource names that currently hold `view`, the resources that must also hold `view`
// for POST/PUT /v4/dash/roles to be accepted. Empty unless some Customer 360 access is granted.
export const customer360PrerequisiteResources = (viewGranted: readonly string[]): string[] => {
  if (!viewGranted.some(name => name.startsWith(CUSTOMER360_RESOURCE))) return [];

  const required = [CUSTOMER_MODULE_RESOURCE, ...CUSTOMER360_CONTAINER_RESOURCES];
  viewGranted
    .filter(name => name.startsWith(`${CUSTOMER360_RESOURCE}.`))
    .forEach(leaf => required.push(...containersForCustomer360Leaf(leaf)));

  return Array.from(new Set(required));
};

// The subset of the prerequisites not yet granted `view` — what the form must tick for the user.
export const missingCustomer360Prerequisites = (viewGranted: readonly string[]): string[] =>
  customer360PrerequisiteResources(viewGranted).filter(name => !viewGranted.includes(name));

// The industry-scoped base cards holding `view`. While any does, the save re-adds the Industry row
// (normalizeValues), so the tree holds that row ticked rather than show a role the payload is not.
export const customer360IndustryRequiredBy = (viewGranted: readonly string[]): string[] =>
  CUSTOMER360_INDUSTRY_SCOPED_LEAVES.filter(leaf => viewGranted.includes(leaf));

export const customer360IndustryLockHint = (requiredBy: readonly string[]): string =>
  `Required by ${requiredBy.map(labelOf).join(' and ')}. Untick ${
    requiredBy.length > 1 ? 'them' : 'it'
  } to remove this permission.`;

export const CUSTOMER_MODULE_LABEL = 'Customer Management → View';

export const customer360PrerequisiteHint = (names: readonly string[]): string => {
  const labels = names
    .map(name => (name === CUSTOMER_MODULE_RESOURCE ? CUSTOMER_MODULE_LABEL : labelOf(name)))
    .join(', ');
  const [subject, object] = names.length > 1 ? ['These were', 'them'] : ['This was', 'it'];

  return [
    `Customer 360 access requires ${labels}.`,
    `${subject} selected for you; the role cannot be saved without ${object}.`
  ].join(' ');
};
